import {
  AppAlert,
  AppButton,
  AppCard,
  AppCheckbox,
  AppDatePicker,
  AppForm,
  AppInputNumber,
  AppModal,
  AppPagination,
  AppPhoneNumber,
  AppProgress,
  AppRadio,
  AppRadioGroup,
  AppSelect,
  AppSkeleton,
  AppTable,
  AppTableActionButton,
  AppTabs,
  AppTag,
  AppTextArea,
  AppTextField,
  AppTooltip,
  AppTypography,
} from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import ServiceTypeSelector from '@/components/molecules/ServiceTypeSelector';
import { RA2NDatePicker } from '@/components/organisms';
import useAppModal from '@/hooks/useAppModal';
import useAppToast from '@/hooks/useAppToast';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2, SERVICE_TYPE_BOTH } from '@/constants/serviceType';
import { type TableProps } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';

/**
 * Clinic Design Template / Style Guide Page
 *
 * This page showcases all UI components and patterns used in RA2N Clinic Portal.
 * Colors change dynamically based on Service Type (A2/R2) selector.
 * Fully integrated with clinic theme system.
 */

const MODAL_SIZE_MAP = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
} as const;

// =============================================================================
// SECTION 5: SAMPLE DATA TYPE
// =============================================================================
interface SampleData {
  id: number;
  name: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  status: 'active' | 'pending' | 'inactive';
  joinDate: string;
  salary: number;
  remarks: string;
}

// Sample data for table
const sampleTableData: SampleData[] = [
  {
    id: 1,
    name: '山田 太郎',
    email: 'yamada@example.com',
    phone: '090-1234-5678',
    department: '営業部',
    position: '部長',
    status: 'active',
    joinDate: '2020-04-01',
    salary: 650000,
    remarks: '優秀な営業成績を持つ。チームリーダーとしての経験豊富。',
  },
  {
    id: 2,
    name: '佐藤 花子',
    email: 'sato@example.com',
    phone: '090-2345-6789',
    department: '開発部',
    position: 'エンジニア',
    status: 'pending',
    joinDate: '2022-07-15',
    salary: 450000,
    remarks: 'フロントエンド開発が得意。React/TypeScript経験3年。',
  },
  {
    id: 3,
    name: '鈴木 一郎',
    email: 'suzuki@example.com',
    phone: '090-3456-7890',
    department: '人事部',
    position: '課長',
    status: 'inactive',
    joinDate: '2018-01-10',
    salary: 550000,
    remarks: '現在休職中。復帰予定は2026年4月。',
  },
  {
    id: 4,
    name: '鈴木 一郎',
    email: 'suzuki@example.com',
    phone: '090-3456-7890',
    department: '人事部',
    position: '課長',
    status: 'inactive',
    joinDate: '2018-01-10',
    salary: 550000,
    remarks: '現在休職中。復帰予定は2026年4月。',
  },
  {
    id: 5,
    name: '鈴木 一郎',
    email: 'suzuki@example.com',
    phone: '090-3456-7890',
    department: '人事部',
    position: '課長',
    status: 'inactive',
    joinDate: '2018-01-10',
    salary: 550000,
    remarks: '現在休職中。復帰予定は2026年4月。',
  },
  {
    id: 6,
    name: '鈴木 一郎',
    email: 'suzuki@example.com',
    phone: '090-3456-7890',
    department: '人事部',
    position: '課長',
    status: 'inactive',
    joinDate: '2018-01-10',
    salary: 550000,
    remarks: '現在休職中。復帰予定は2026年4月。',
  },
  {
    id: 7,
    name: '鈴木 一郎',
    email: 'suzuki@example.com',
    phone: '090-3456-7890',
    department: '人事部',
    position: '課長',
    status: 'inactive',
    joinDate: '2018-01-10',
    salary: 550000,
    remarks: '現在休職中。復帰予定は2026年4月。',
  },
  {
    id: 8,
    name: '鈴木 一郎',
    email: 'suzuki@example.com',
    phone: '090-3456-7890',
    department: '人事部',
    position: '課長',
    status: 'inactive',
    joinDate: '2018-01-10',
    salary: 550000,
    remarks: '現在休職中。復帰予定は2026年4月。',
  },
];

// =============================================================================
// SECTION 6: DETAIL MODAL COMPONENT
// =============================================================================
interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: SampleData | null;
}

const DetailModal: React.FC<DetailModalProps> = ({ isOpen, onClose, data }) => {
  if (!data) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-800',
    };
    const labels = {
      active: '有効',
      pending: '保留',
      inactive: '無効',
    };
    return (
      <span
        className={`px-3 py-1 text-sm font-medium rounded-full ${
          styles[status as keyof typeof styles]
        }`}
      >
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  return (
    <AppModal isOpen={isOpen} onClose={onClose} title="詳細情報" maxWidth="max-w-3xl">
      <AppCard title="基本情報" isHeaderColor className="mb-6">
        <div className="overflow-hidden rounded-b-lg">
          <table className="w-full">
            <tbody>
              <tr className="border-b">
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">ID</th>
                <td className="px-4 py-3 text-sm">{data.id}</td>
              </tr>
              <tr className="border-b">
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">名前</th>
                <td className="px-4 py-3 text-sm font-medium">{data.name}</td>
              </tr>
              <tr className="border-b">
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">メール</th>
                <td className="px-4 py-3 text-sm">
                  <a href={`mailto:${data.email}`} className="text-blue-600 hover:underline">
                    {data.email}
                  </a>
                </td>
              </tr>
              <tr>
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">
                  電話番号
                </th>
                <td className="px-4 py-3 text-sm">{data.phone}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </AppCard>

      <AppCard title="勤務情報" isHeaderColor className="mb-6">
        <div className="overflow-hidden rounded-b-lg">
          <table className="w-full">
            <tbody>
              <tr className="border-b">
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">部署</th>
                <td className="px-4 py-3 text-sm">{data.department}</td>
              </tr>
              <tr className="border-b">
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">役職</th>
                <td className="px-4 py-3 text-sm">{data.position}</td>
              </tr>
              <tr className="border-b">
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">入社日</th>
                <td className="px-4 py-3 text-sm">{formatDate(data.joinDate)}</td>
              </tr>
              <tr className="border-b">
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">給与</th>
                <td className="px-4 py-3 text-sm font-medium">{formatCurrency(data.salary)}</td>
              </tr>
              <tr>
                <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">
                  ステータス
                </th>
                <td className="px-4 py-3 text-sm">{getStatusBadge(data.status)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </AppCard>

      <AppCard title="備考" isHeaderColor className="mb-6">
        <div className="p-4 rounded-b-lg bg-gray-50">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {data.remarks || '(備考なし)'}
          </p>
        </div>
      </AppCard>
    </AppModal>
  );
};

// =============================================================================
// SECTION 7: EDIT MODAL COMPONENT
// =============================================================================
interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: SampleData | null;
  onSave: (data: SampleData) => void;
}

const STATUS_LABELS: Record<SampleData['status'], string> = {
  active: '有効',
  pending: '保留',
  inactive: '無効',
};

const EditModal: React.FC<EditModalProps> = ({ isOpen, onClose, data, onSave }) => {
  const [formData, setFormData] = useState<SampleData | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);

  // Initialize form data and reset to view mode when modal opens
  React.useEffect(() => {
    if (data) {
      setFormData({ ...data });
      setErrors({});
      setIsEditMode(false);
    }
  }, [data]);

  if (!formData) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => (prev ? { ...prev, [name]: value } : null));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => (prev ? { ...prev, [name]: parseInt(value) || 0 } : null));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = '名前は必須です';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'メールは必須です';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = '電話番号は必須です';
    }
    if (!formData.department.trim()) {
      newErrors.department = '部署は必須です';
    }
    if (formData.salary <= 0) {
      newErrors.salary = '給与は0より大きい値を入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(formData);
      setIsEditMode(false);
    }
  };

  const handleEditSubmit = () => {
    if (isEditMode) {
      handleSubmit();
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    onClose();
  };

  const renderDetailField = (label: string, value: React.ReactNode) => (
    <div key={label} className="pb-2 border-b border-gray-100 last:border-0 last:pb-0">
      <div className="mb-0.5 text-base font-medium uppercase tracking-wide text-gray-400">
        {label}
      </div>
      <div className="text-base font-medium text-gray-900">{value ?? '—'}</div>
    </div>
  );

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? '編集' : '詳細'}
      maxWidth="max-w-4xl"
      onOk={isEditMode ? handleEditSubmit : undefined}
      footer={
        isEditMode ? (
          <div className="flex justify-end flex-1 w-full gap-2">
            <AppButton type="primary" onClick={handleEditSubmit}>
              適用
            </AppButton>
          </div>
        ) : (
          <div className="flex justify-end flex-1 w-full gap-2">
            <AppButton type="primary" onClick={() => setIsEditMode(true)}>
              編集
            </AppButton>
          </div>
        )
      }
    >
      {isEditMode ? (
        <>
          <AppCard title="基本情報" isHeaderColor className="mb-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">ID</label>
                <AppTextField className="w-full" value={String(formData.id)} disabled />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  名前 <span className="text-red-500">*</span>
                </label>
                <AppForm.Item validateStatus={errors.name ? 'error' : undefined} help={errors.name}>
                  <AppTextField
                    className="w-full"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </AppForm.Item>
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  メール <span className="text-red-500">*</span>
                </label>
                <AppTextField
                  className="w-full"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  status={errors.email ? 'error' : undefined}
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <AppTextField
                  className="w-full"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  status={errors.phone ? 'error' : undefined}
                />
                {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
              </div>
            </div>
          </AppCard>

          <AppCard title="勤務情報" isHeaderColor className="mb-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  部署 <span className="text-red-500">*</span>
                </label>
                <AppSelect
                  className="w-full"
                  value={formData.department || undefined}
                  onChange={value =>
                    setFormData(prev =>
                      prev ? { ...prev, department: String(value ?? '') } : null
                    )
                  }
                  status={errors.department ? 'error' : undefined}
                  options={[
                    { value: '', label: '選択してください' },
                    { value: '営業部', label: '営業部' },
                    { value: '開発部', label: '開発部' },
                    { value: '人事部', label: '人事部' },
                    { value: '経理部', label: '経理部' },
                    { value: '総務部', label: '総務部' },
                  ]}
                />
                {errors.department && (
                  <p className="mt-1 text-sm text-red-500">{errors.department}</p>
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">役職</label>
                <AppSelect
                  className="w-full"
                  value={formData.position || undefined}
                  onChange={value =>
                    setFormData(prev => (prev ? { ...prev, position: String(value ?? '') } : null))
                  }
                  options={[
                    { value: '', label: '選択してください' },
                    { value: '社員', label: '社員' },
                    { value: '主任', label: '主任' },
                    { value: '係長', label: '係長' },
                    { value: '課長', label: '課長' },
                    { value: '部長', label: '部長' },
                    { value: 'エンジニア', label: 'エンジニア' },
                  ]}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">入社日</label>
                <AppDatePicker
                  className="w-full"
                  value={formData.joinDate ? dayjs(formData.joinDate) : null}
                  onChange={(_, dateStr) =>
                    setFormData(prev =>
                      prev ? { ...prev, joinDate: (dateStr as string) || '' } : null
                    )
                  }
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  給与 <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center">
                  <span className="px-2">¥</span>
                  <AppInputNumber
                    className="w-full"
                    value={formData.salary}
                    onChange={val =>
                      handleNumberChange({
                        target: { name: 'salary', value: String(val ?? 0) },
                      } as any)
                    }
                    status={errors.salary ? 'error' : undefined}
                  />
                </div>
                {errors.salary && <p className="mt-1 text-sm text-red-500">{errors.salary}</p>}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">ステータス</label>
                <AppSelect
                  className="w-full"
                  value={formData.status}
                  onChange={value =>
                    setFormData(prev =>
                      prev ? { ...prev, status: (value as SampleData['status']) ?? 'active' } : null
                    )
                  }
                  options={[
                    { value: 'active', label: '有効' },
                    { value: 'pending', label: '保留' },
                    { value: 'inactive', label: '無効' },
                  ]}
                />
              </div>
            </div>
          </AppCard>

          <AppCard title="備考" isHeaderColor className="mb-6">
            <div className="mt-2">
              <AppTextArea
                className="w-full"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows={4}
                placeholder="備考を入力..."
              />
            </div>
          </AppCard>
        </>
      ) : (
        <>
          <AppCard title="基本情報" isHeaderColor className="mb-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {renderDetailField('ID', String(formData.id))}
              {renderDetailField('名前', formData.name)}
              {renderDetailField('メール', formData.email)}
              {renderDetailField('電話番号', formData.phone)}
            </div>
          </AppCard>

          <AppCard title="勤務情報" isHeaderColor className="mb-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {renderDetailField('部署', formData.department)}
              {renderDetailField('役職', formData.position)}
              {renderDetailField('入社日', formData.joinDate)}
              {renderDetailField('給与', `¥${formData.salary.toLocaleString()}`)}
              {renderDetailField('ステータス', STATUS_LABELS[formData.status])}
            </div>
          </AppCard>

          <AppCard title="備考" isHeaderColor className="mb-6">
            <div className="mt-2">
              <div className="text-gray-900 whitespace-pre-wrap">{formData.remarks || '—'}</div>
            </div>
          </AppCard>
        </>
      )}
    </AppModal>
  );
};

// =============================================================================
// MAIN CLINIC DESIGN TEMPLATE PAGE
// =============================================================================
const ClinicDesignTemplatePage: React.FC = () => {
  const {
    colors,
    serviceType,
    siteType,
    themeConfig,
    canSwitchServiceType,
    setServiceType,
    clinicServiceType,
    theme,
  } = useTheme();

  // Get colors for both A2 and R2 for comparison
  const a2Colors = themeConfig.A2.colors;
  const r2Colors = themeConfig.R2.colors;

  // State for demos
  const [checkboxValues, setCheckboxValues] = useState({
    option1: true,
    option2: false,
    option3: false,
  });
  const [radioValue, setRadioValue] = useState('option1');
  const [selectValue, setSelectValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [ra2DateValue, setRa2DateValue] = useState('');
  const [switchValue, setSwitchValue] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(5);

  // State for table data and modals
  const [tableData, setTableData] = useState<SampleData[]>(sampleTableData);
  const [selectedData, setSelectedData] = useState<SampleData | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Handlers for table actions
  const handleViewDetail = (data: SampleData) => {
    setSelectedData(data);
    setIsDetailModalOpen(true);
  };

  const handleEdit = (data: SampleData) => {
    setSelectedData(data);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = (updatedData: SampleData) => {
    setTableData(prev => prev.map(item => (item.id === updatedData.id ? updatedData : item)));
    setSelectedData(updatedData);
    alert('保存しました！');
  };

  const { confirm } = useAppModal();

  const handleDeleteClick = (record: SampleData) => {
    confirm({
      title: '削除確認',
      content: `「${record.name}」を削除してもよろしいですか？`,
      okButtonProps: { danger: true },
      onOk: () => {
        setTableData(prev => prev.filter(item => item.id !== record.id));
        toast.success(`「${record.name}」を削除しました。`);
      },
    });
  };

  // Sample tabs (Ant Design Tabs items)
  const tabItems = [
    { key: 'tab1', label: '基本情報', children: <div>基本情報の内容がここに表示されます。</div> },
    { key: 'tab2', label: '詳細設定', children: <div>詳細設定の内容がここに表示されます。</div> },
    { key: 'tab3', label: '履歴', children: <div>履歴の内容がここに表示されます。</div> },
  ];

  // Table columns for Section 6 (AppTable) – no fixed width so columns size to content
  const tableColumns: TableProps<SampleData>['columns'] = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', align: 'center' },
      {
        title: '名前',
        dataIndex: 'name',
        key: 'name',
        render: (_, r) => <span className="font-medium">{r.name}</span>,
      },
      { title: '部署', dataIndex: 'department', key: 'department' },
      {
        title: 'ステータス',
        dataIndex: 'status',
        key: 'status',
        render: (_, r) => (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              r.status === 'active'
                ? 'bg-green-100 text-green-800'
                : r.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
            }`}
          >
            {r.status === 'active' ? '有効' : r.status === 'pending' ? '保留' : '無効'}
          </span>
        ),
      },
      {
        title: '入社日',
        dataIndex: 'joinDate',
        key: 'joinDate',
        className: 'text-gray-500',
      },
      {
        title: '操作',
        key: 'action',
        align: 'center',
        className: '!w-0 whitespace-nowrap',
        render: (_, record) => (
          <div className="flex justify-center gap-1 px-2 w-fit">
            <AppTableActionButton
              action="detail"
              onClick={() => handleViewDetail(record)}
              title="詳細表示"
            >
              詳細
            </AppTableActionButton>
            <AppTableActionButton action="edit" onClick={() => handleEdit(record)} title="編集">
              編集
            </AppTableActionButton>
            <AppTableActionButton
              action="delete"
              onClick={() => handleDeleteClick(record)}
              title="削除"
            >
              削除
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    [handleViewDetail, handleEdit, handleDeleteClick]
  );
  const toast = useAppToast();

  return (
    <div className="mx-auto max-w-7xl">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Design Template / Style Guide</h1>
        <p className="mt-1 text-gray-600">
          RA2N Clinic Portal UIコンポーネント一覧 - {themeConfig.siteName} /{' '}
          {serviceType === SERVICE_TYPE_A2 ? 'はり・きゅう' : 'マッサージ'}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          ※ サイドバー下部の Service Type Selector で A2/R2 を切り替えると、カラーが変わります
        </p>
      </div>

      {/* ================================================================= */}
      {/* SECTION 0: THEME INFO & SERVICE TYPE SELECTOR */}
      {/* ================================================================= */}
      <CollapsibleCard title="0. Theme Info & Service Type Selector (テーマ情報)">
        <div className="space-y-6">
          {/* Current Theme Info */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="p-3 rounded-lg bg-gray-50">
              <p className="text-xs tracking-wide text-gray-500 uppercase">Site Type</p>
              <p className="text-lg font-bold">{siteType}</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50">
              <p className="text-xs tracking-wide text-gray-500 uppercase">Site Name</p>
              <p className="text-lg font-bold">{themeConfig.siteName}</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50">
              <p className="text-xs tracking-wide text-gray-500 uppercase">Service Type</p>
              <p className="text-lg font-bold">
                {serviceType} ({serviceType === SERVICE_TYPE_A2 ? 'Acupuncture' : 'Massage'})
              </p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50">
              <p className="text-xs tracking-wide text-gray-500 uppercase">Clinic Restriction</p>
              <p className="text-lg font-bold">{clinicServiceType || SERVICE_TYPE_BOTH}</p>
            </div>
          </div>

          {/* Service Type Selector */}
          <div>
            <h4 className="mb-3 font-medium">Switch Service Type (A2/R2)</h4>
            <ServiceTypeSelector
              variant="inline"
              currentType={serviceType}
              onTypeChange={setServiceType}
              canSwitch={canSwitchServiceType}
              a2Colors={a2Colors}
              r2Colors={r2Colors}
              themeConfig={themeConfig}
            />
          </div>

          {/* A2/R2 Color Comparison */}
          <div>
            <h4 className="mb-3 font-medium">A2/R2 Color Comparison</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* A2 Colors */}
              <div
                className={`p-4 rounded-lg border-2 ${
                  serviceType === SERVICE_TYPE_A2 ? 'border-gray-400' : 'border-transparent'
                }`}
              >
                <h5 className="flex items-center gap-2 mb-3 font-bold">
                  <span
                    className="px-2 py-1 text-xs text-white rounded"
                    style={{ backgroundColor: a2Colors.primary }}
                  >
                    A2
                  </span>
                  {themeConfig.A2.displayName}
                  {serviceType === SERVICE_TYPE_A2 && (
                    <span className="text-xs text-gray-500">(Active)</span>
                  )}
                </h5>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <div
                      className="h-10 rounded"
                      style={{ backgroundColor: a2Colors.primary }}
                    ></div>
                    <p className="mt-1 text-xs">Primary</p>
                  </div>
                  <div className="text-center">
                    <div
                      className="h-10 rounded"
                      style={{ backgroundColor: a2Colors.secondary }}
                    ></div>
                    <p className="mt-1 text-xs">Secondary</p>
                  </div>
                  <div className="text-center">
                    <div
                      className="h-10 rounded"
                      style={{ backgroundColor: a2Colors.sidebarBg }}
                    ></div>
                    <p className="mt-1 text-xs">Sidebar</p>
                  </div>
                  <div className="text-center">
                    <div
                      className="h-10 rounded"
                      style={{ backgroundColor: a2Colors.sectionHeader }}
                    ></div>
                    <p className="mt-1 text-xs">Section</p>
                  </div>
                </div>
              </div>

              {/* R2 Colors */}
              <div
                className={`p-4 rounded-lg border-2 ${
                  serviceType === SERVICE_TYPE_R2 ? 'border-gray-400' : 'border-transparent'
                }`}
              >
                <h5 className="flex items-center gap-2 mb-3 font-bold">
                  <span
                    className="px-2 py-1 text-xs text-white rounded"
                    style={{ backgroundColor: r2Colors.primary }}
                  >
                    R2
                  </span>
                  {themeConfig.R2.displayName}
                  {serviceType === SERVICE_TYPE_R2 && (
                    <span className="text-xs text-gray-500">(Active)</span>
                  )}
                </h5>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <div
                      className="h-10 rounded"
                      style={{ backgroundColor: r2Colors.primary }}
                    ></div>
                    <p className="mt-1 text-xs">Primary</p>
                  </div>
                  <div className="text-center">
                    <div
                      className="h-10 rounded"
                      style={{ backgroundColor: r2Colors.secondary }}
                    ></div>
                    <p className="mt-1 text-xs">Secondary</p>
                  </div>
                  <div className="text-center">
                    <div
                      className="h-10 rounded"
                      style={{ backgroundColor: r2Colors.sidebarBg }}
                    ></div>
                    <p className="mt-1 text-xs">Sidebar</p>
                  </div>
                  <div className="text-center">
                    <div
                      className="h-10 rounded"
                      style={{ backgroundColor: r2Colors.sectionHeader }}
                    ></div>
                    <p className="mt-1 text-xs">Section</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Theme Logo */}
          <div>
            <h4 className="mb-3 font-medium">Current Logo</h4>
            <div className="flex items-center gap-4 p-4 bg-gray-100 rounded-lg">
              <img
                src={theme.logoPath}
                alt={`${serviceType} Logo`}
                className="object-contain max-h-12"
                onError={e => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <span className="text-sm text-gray-600">{theme.logoPath}</span>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 1: COLOR PALETTE */}
      {/* ================================================================= */}
      <CollapsibleCard title="1. Color Palette (カラーパレット)">
        <div className="p-3 mb-4 rounded-lg" style={{ backgroundColor: colors.primary + '20' }}>
          <p className="text-sm font-medium">
            現在のテーマ: <strong>{siteType}</strong> /{' '}
            <strong>
              {serviceType === SERVICE_TYPE_A2 ? 'A2 (はり・きゅう)' : 'R2 (マッサージ)'}
            </strong>
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="text-center">
            <div className="h-16 mb-2 rounded-lg" style={{ backgroundColor: colors.primary }}></div>
            <p className="text-sm font-medium">Primary</p>
            <p className="text-xs text-gray-500">{colors.primary}</p>
          </div>
          <div className="text-center">
            <div
              className="h-16 mb-2 rounded-lg"
              style={{ backgroundColor: colors.primaryDark }}
            ></div>
            <p className="text-sm font-medium">Primary Dark</p>
            <p className="text-xs text-gray-500">{colors.primaryDark}</p>
          </div>
          <div className="text-center">
            <div
              className="h-16 mb-2 rounded-lg"
              style={{ backgroundColor: colors.secondary }}
            ></div>
            <p className="text-sm font-medium">Secondary</p>
            <p className="text-xs text-gray-500">{colors.secondary}</p>
          </div>
          <div className="text-center">
            <div
              className="h-16 mb-2 rounded-lg"
              style={{ backgroundColor: colors.sectionHeader }}
            ></div>
            <p className="text-sm font-medium">Section Header</p>
            <p className="text-xs text-gray-500">{colors.sectionHeader}</p>
          </div>
          <div className="text-center">
            <div
              className="h-16 mb-2 rounded-lg"
              style={{ backgroundColor: colors.sidebarBg }}
            ></div>
            <p className="text-sm font-medium">Sidebar BG</p>
            <p className="text-xs text-gray-500">{colors.sidebarBg}</p>
          </div>
          <div className="text-center">
            <div
              className="h-16 mb-2 border rounded-lg"
              style={{ backgroundColor: colors.menuOpen }}
            ></div>
            <p className="text-sm font-medium">Menu Open</p>
            <p className="text-xs text-gray-500">{colors.menuOpen}</p>
          </div>
          <div className="text-center">
            <div
              className="h-16 mb-2 border rounded-lg"
              style={{ backgroundColor: colors.menuHover }}
            ></div>
            <p className="text-sm font-medium">Menu Hover</p>
            <p className="text-xs text-gray-500">{colors.menuHover}</p>
          </div>
          <div className="text-center">
            <div
              className="h-16 mb-2 border rounded-lg"
              style={{ backgroundColor: colors.menuActive }}
            ></div>
            <p className="text-sm font-medium">Menu Active</p>
            <p className="text-xs text-gray-500">{colors.menuActive}</p>
          </div>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 2: TYPOGRAPHY */}
      {/* ================================================================= */}
      <CollapsibleCard title="2. Typography (タイポグラフィ)">
        <div className="space-y-4">
          <div>
            <AppTypography.H1>Heading 1 - 見出し1 (text-4xl font-bold)</AppTypography.H1>
          </div>
          <div>
            <AppTypography.H2>Heading 2 - 見出し2 (text-3xl font-bold)</AppTypography.H2>
          </div>
          <div>
            <AppTypography.H3>Heading 3 - 見出し3 (text-2xl font-bold)</AppTypography.H3>
          </div>
          <div>
            <AppTypography.H4>Heading 4 - 見出し4 (text-xl font-semibold)</AppTypography.H4>
          </div>
          <div>
            <AppTypography.H5>Heading 5 - 見出し5 (text-lg font-medium)</AppTypography.H5>
          </div>
          <div>
            <AppTypography.P>Body Text - 本文テキスト (text-base)</AppTypography.P>
          </div>
          <div>
            <AppTypography.Small>
              Small Text - 小さいテキスト (text-sm text-gray-600)
            </AppTypography.Small>
          </div>
          <div>
            <AppTypography.XS>Extra Small - 極小テキスト (text-xs text-gray-500)</AppTypography.XS>
          </div>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 3: BUTTONS */}
      {/* ================================================================= */}
      <CollapsibleCard title="3. Buttons (ボタン)">
        <div className="space-y-6">
          {/* Primary Buttons */}
          <div>
            <h4 className="mb-3 font-medium">Primary Buttons</h4>
            <div className="flex flex-wrap gap-3">
              <AppButton
                type="primary"
                className="px-4 py-2 font-medium text-white transition-opacity rounded hover:opacity-90"
                style={{ backgroundColor: colors.secondary }}
              >
                Primary Button
              </AppButton>
              <AppButton
                type="primary"
                className="px-4 py-2 font-medium text-white transition-opacity rounded hover:opacity-90"
                style={{ backgroundColor: colors.primary }}
              >
                Header Color
              </AppButton>
              <AppButton
                type="primary"
                className="px-4 py-2 font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Blue Button
              </AppButton>
              <AppButton
                type="primary"
                className="px-4 py-2 font-medium text-white bg-green-600 rounded hover:bg-green-700"
              >
                Green Button
              </AppButton>
            </div>
          </div>

          {/* Secondary/Outline Buttons */}
          <div>
            <h4 className="mb-3 font-medium">Secondary / Outline Buttons</h4>
            <div className="flex flex-wrap gap-3">
              <AppButton
                type="default"
                className="px-4 py-2 font-medium text-gray-800 bg-gray-200 rounded hover:bg-gray-300"
              >
                Secondary
              </AppButton>
              <AppButton
                type="default"
                className="px-4 py-2 font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
              >
                Outline
              </AppButton>
              <AppButton
                type="default"
                className="px-4 py-2 font-medium border-2 rounded"
                style={{ borderColor: colors.secondary, color: colors.secondary }}
              >
                Theme Outline
              </AppButton>
            </div>
          </div>

          {/* Danger Buttons */}
          <div>
            <h4 className="mb-3 font-medium">Danger / Warning Buttons</h4>
            <div className="flex flex-wrap gap-3">
              <AppButton
                danger
                type="primary"
                className="px-4 py-2 font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                Delete
              </AppButton>
              <AppButton
                type="primary"
                className="px-4 py-2 font-medium text-white bg-yellow-500 rounded hover:bg-yellow-600"
              >
                Warning
              </AppButton>
              <AppButton
                danger
                type="default"
                className="px-4 py-2 font-medium text-red-600 border border-red-600 rounded hover:bg-red-50"
              >
                Cancel
              </AppButton>
            </div>
          </div>

          {/* Button Sizes */}
          <div>
            <h4 className="mb-3 font-medium">Button Sizes</h4>
            <div className="flex flex-wrap items-center gap-3">
              <AppButton
                type="primary"
                size="small"
                className="px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Extra Small
              </AppButton>
              <AppButton
                type="primary"
                size="small"
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Small
              </AppButton>
              <AppButton
                type="primary"
                className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Normal
              </AppButton>
              <AppButton
                type="primary"
                size="large"
                className="px-6 py-3 text-lg text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Large
              </AppButton>
            </div>
          </div>

          {/* Button States */}
          <div>
            <h4 className="mb-3 font-medium">Button States</h4>
            <div className="flex flex-wrap gap-3">
              <AppButton
                type="primary"
                className="px-4 py-2 font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Normal
              </AppButton>
              <AppButton
                type="primary"
                disabled
                className="px-4 py-2 font-medium text-white bg-blue-600 rounded opacity-50 cursor-not-allowed"
              >
                Disabled
              </AppButton>
              <AppButton
                type="primary"
                loading
                className="flex items-center gap-2 px-4 py-2 font-medium text-white bg-blue-600 rounded"
              >
                Loading...
              </AppButton>
            </div>
          </div>

          {/* Icon Buttons */}
          <div>
            <h4 className="mb-3 font-medium">Icon Buttons</h4>
            <div className="flex flex-wrap gap-3">
              <AppButton
                type="primary"
                className="flex items-center gap-2 px-4 py-2 font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                <span>🔍</span> 検索
              </AppButton>
              <AppButton
                type="primary"
                className="flex items-center gap-2 px-4 py-2 font-medium text-white bg-green-600 rounded hover:bg-green-700"
              >
                <span>➕</span> 新規作成
              </AppButton>
              <AppButton
                type="primary"
                className="flex items-center gap-2 px-4 py-2 font-medium text-white bg-gray-600 rounded hover:bg-gray-700"
              >
                <span>📥</span> ダウンロード
              </AppButton>
              <AppButton
                type="default"
                className="p-2 bg-gray-200 rounded hover:bg-gray-300"
                title="Edit"
              >
                ✏️
              </AppButton>
              <AppButton
                type="default"
                className="p-2 bg-gray-200 rounded hover:bg-gray-300"
                title="Copy"
              >
                📋
              </AppButton>
              <AppButton
                danger
                type="default"
                className="p-2 text-red-600 bg-red-100 rounded hover:bg-red-200"
                title="Delete"
              >
                🗑️
              </AppButton>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 4: FORM CONTROLS */}
      {/* ================================================================= */}
      <CollapsibleCard title="4. Form Controls (フォームコントロール)">
        <div className="space-y-6">
          {/* Phone Number Input */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Phone Number</label>
            <AppPhoneNumber
              className="w-full md:w-1/2"
              value={phoneValue}
              onChange={setPhoneValue}
              placeholder="090-1234-5678"
            />
            <p className="mt-1 text-xs text-gray-500">
              Fullwidth → halfwidth auto-convert, digits + dashes only, max 13 chars
            </p>
          </div>

          {/* Text Input */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Text Input <span className="text-red-500">*</span>
              </label>
              <AppTextField
                className="w-full"
                value={textValue}
                onChange={e => setTextValue(e.target.value)}
                placeholder="テキストを入力..."
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Disabled Input</label>
              <AppTextField className="w-full" value="編集不可" disabled />
            </div>
          </div>

          {/* Input with Error */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Input with Error</label>
            <AppTextField className="w-full" status="error" placeholder="エラーのある入力" />
            <p className="mt-1 text-sm text-red-500">このフィールドは必須です。</p>
          </div>

          {/* Select / Dropdown */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Select / Dropdown
              </label>
              <AppSelect
                className="w-full"
                value={selectValue || undefined}
                onChange={value => setSelectValue(value != null ? String(value) : '')}
                options={[
                  { value: '', label: '選択してください...' },
                  { value: 'option1', label: 'オプション 1' },
                  { value: 'option2', label: 'オプション 2' },
                  { value: 'option3', label: 'オプション 3' },
                ]}
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Date Input</label>
              <AppDatePicker
                className="w-full mb-4"
                value={dateValue ? dayjs(dateValue) : null}
                onChange={date => {
                  const d = date && !Array.isArray(date) ? date : null;
                  setDateValue(d ? d.format('YYYY-MM-DD') : '');
                }}
              />
              <RA2NDatePicker
                picker="day"
                className="w-full"
                value={ra2DateValue}
                onChange={setRa2DateValue}
              />
            </div>
          </div>

          {/* Textarea */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Textarea</label>
            <AppTextArea
              className="w-full"
              value={textareaValue}
              onChange={e => setTextareaValue(e.target.value)}
              rows={4}
              placeholder="複数行のテキストを入力..."
            />
          </div>

          {/* Checkbox */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Checkbox</label>
            <div className="flex flex-col space-y-2">
              {Object.entries(checkboxValues).map(([key, value]) => (
                <AppCheckbox
                  key={key}
                  checked={value}
                  onChange={e => setCheckboxValues({ ...checkboxValues, [key]: e.target.checked })}
                >
                  <span>
                    {key === 'option1'
                      ? 'チェックボックス 1'
                      : key === 'option2'
                        ? 'チェックボックス 2'
                        : 'チェックボックス 3'}
                  </span>
                </AppCheckbox>
              ))}
            </div>
          </div>

          {/* Radio Button */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Radio Button</label>
            <AppRadioGroup
              value={radioValue}
              onChange={e => setRadioValue(e.target.value)}
              className="flex flex-col gap-2"
            >
              {['option1', 'option2', 'option3'].map(option => (
                <AppRadio key={option} value={option}>
                  ラジオボタン {option.replace('option', '')}
                </AppRadio>
              ))}
            </AppRadioGroup>
          </div>

          {/* Toggle Switch */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Toggle Switch (Theme-aware)
            </label>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSwitchValue(!switchValue)}
                className="relative inline-flex items-center h-6 transition-colors rounded-full w-11"
                style={{ backgroundColor: switchValue ? colors.secondary : '#d1d5db' }}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    switchValue ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">{switchValue ? 'ON' : 'OFF'}</span>
              <span className="text-xs text-gray-400">
                (uses colors.secondary: {colors.secondary})
              </span>
            </div>
          </div>

          {/* Input Group */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Input Group (Prefix/Suffix)
            </label>
            <AppTextField
              className="w-full"
              addonBefore="¥"
              addonAfter="円"
              type="number"
              placeholder="金額"
            />
          </div>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 5: TAB CONTROL */}
      {/* ================================================================= */}
      <CollapsibleCard title="5. Tab Control (タブコントロール)">
        <div
          className="design-template-tabs-primary-wrapper"
          style={{ ['--tab-primary']: colors.primary } as React.CSSProperties}
        >
          <AppTabs
            items={tabItems}
            classNames={{
              header: 'design-template-tab-bar-primary',
              item: 'design-template-tab-item-primary',
            }}
            styles={{
              header: { marginBottom: 0, paddingLeft: 0 },
              item: {
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                paddingLeft: 12,
                paddingRight: 12,
                margin: 0,
              },
            }}
          />
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 6: TABLES */}
      {/* ================================================================= */}
      <CollapsibleCard title="6. Data Table (データテーブル) - Click buttons to see Edit/Detail modals">
        <p className="mb-4 text-sm text-gray-600">
          下記のテーブルの「詳細」「編集」ボタンをクリックすると、それぞれのモーダルが表示されます。
        </p>
        <div className="app-table-last-col-fit">
          <AppTable
            columns={tableColumns}
            dataSource={tableData}
            rowKey="id"
            pagination={false}
            tableLayout="auto"
          />
        </div>
        <AppPagination
          className="justify-center mt-4"
          current={currentPage}
          total={20}
          pageSize={1}
          onChange={setCurrentPage}
        />

        {/* Detail Modal */}
        <DetailModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          data={selectedData}
        />

        {/* Edit Modal */}
        <EditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          data={selectedData}
          onSave={handleSaveEdit}
        />
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 7: CARDS & PANELS */}
      {/* ================================================================= */}
      <CollapsibleCard title="7. Cards & Panels (カード・パネル)">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Simple Card */}
          <AppCard title="Simple Card">
            <p className="text-sm text-gray-600">シンプルなカードコンポーネントです。</p>
          </AppCard>

          {/* Card with Header */}
          <AppCard title="Card with Header" isHeaderColor={true}>
            <p className="text-sm text-gray-600">ヘッダー付きカードです。</p>
          </AppCard>

          {/* Stat Card */}
          <AppCard>
            <div className="w-12 h-12 mb-3 bg-blue-500 rounded-full"></div>
            <h4 className="text-sm text-gray-500">統計カード</h4>
            <p className="text-3xl font-bold">1,234</p>
          </AppCard>
        </div>

        {/* Fieldset */}
        <div className="mt-6">
          <fieldset className="p-4 border rounded-lg">
            <legend className="px-2 font-bold text-gray-700">Fieldset (グループ化)</legend>
            <p className="text-gray-600">フォームフィールドをグループ化するためのfieldsetです。</p>
          </fieldset>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 8: ALERTS & MESSAGES (THEME-AWARE) */}
      {/* ================================================================= */}
      <CollapsibleCard title="8. Alerts & Messages (アラート・メッセージ) - Theme-aware">
        <div className="space-y-6">
          {/* Standard Alerts – using AppAlert component */}
          <div>
            <p className="mb-3 text-sm font-medium text-gray-700">Standard Alerts</p>
            <div className="space-y-3">
              <AppAlert title="情報メッセージです。" type="info" showIcon />
              <AppAlert title="成功メッセージです。" type="success" showIcon />
              <AppAlert title="警告メッセージです。" type="warning" showIcon />
              <AppAlert title="エラーメッセージです。" type="error" showIcon />
            </div>
          </div>

          {/* Toast (Standard Alerts) – project-wide toast API */}
          <div>
            <p className="mb-3 text-sm font-medium text-gray-700">
              Toast (Standard Alerts) – 画面上部右に表示
            </p>
            <p className="mb-3 text-xs text-gray-500">
              プロジェクト全体で toast.info / success / warning / error を使用できます。
            </p>
            <div className="flex flex-wrap gap-2">
              <AppButton
                type="default"
                onClick={() => toast.info('情報メッセージです。')}
                className="rounded bg-blue-100 px-3 py-1.5 text-sm font-medium text-blue-800 hover:bg-blue-200"
              >
                Info
              </AppButton>
              <AppButton
                type="default"
                onClick={() => toast.success('成功メッセージです。')}
                className="rounded bg-green-100 px-3 py-1.5 text-sm font-medium text-green-800 hover:bg-green-200"
              >
                Success
              </AppButton>
              <AppButton
                type="default"
                onClick={() => toast.warning('警告メッセージです。')}
                className="rounded bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-200"
              >
                Warning
              </AppButton>
              <AppButton
                type="default"
                onClick={() => toast.error('エラーメッセージです。')}
                className="rounded bg-red-100 px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Error
              </AppButton>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 9: BADGES (THEME-AWARE) */}
      {/* ================================================================= */}
      <CollapsibleCard title="9. Badges & Tags (バッジ・タグ) - Theme-aware">
        <div className="space-y-4">
          {/* Standard Badges */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Standard Badges</p>
            <div className="flex flex-wrap gap-3">
              <AppTag color="blue">Blue Badge</AppTag>
              <AppTag color="green">Green Badge</AppTag>
              <AppTag color="yellow">Yellow Badge</AppTag>
              <AppTag color="red">Red Badge</AppTag>
              <AppTag color="gray">Gray Badge</AppTag>
            </div>
          </div>

          {/* Theme-aware Badges */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">
              Theme-aware Badges (changes with A2/R2)
            </p>
            <div className="flex flex-wrap gap-3">
              <AppTag color={colors.primary}>Primary ({colors.primary})</AppTag>
              <AppTag color={colors.secondary}>Secondary ({colors.secondary})</AppTag>
              <AppTag color={colors.sidebarBg}>Sidebar ({colors.sidebarBg})</AppTag>
              <AppTag color={colors.sectionHeader}>Section ({colors.sectionHeader})</AppTag>
            </div>
          </div>

          {/* Outline Badges */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Outline Badges (Theme-aware)</p>
            <div className="flex flex-wrap gap-3">
              <span
                className="px-3 py-1 text-xs font-medium border-2 rounded-full"
                style={{ borderColor: colors.primary, color: colors.primary }}
              >
                Primary Outline
              </span>
              <span
                className="px-3 py-1 text-xs font-medium border-2 rounded-full"
                style={{ borderColor: colors.secondary, color: colors.secondary }}
              >
                Secondary Outline
              </span>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 10: MODAL */}
      {/* ================================================================= */}
      <CollapsibleCard title="10. Modal (モーダル)">
        <button
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
        >
          Open Modal
        </button>

        <AppModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Sample Modal"
          maxWidth={MODAL_SIZE_MAP.lg}
        >
          <div className="space-y-4">
            <p>これはモーダルのサンプルです。</p>

            <fieldset className="p-4 border rounded">
              <legend className="px-2 font-bold">基本情報</legend>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">名前</label>
                  <AppTextField className="w-full" placeholder="名前" />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">メール</label>
                  <AppTextField className="w-full" type="email" placeholder="メール" />
                </div>
              </div>
            </fieldset>
          </div>
        </AppModal>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 11: LAYOUT PATTERNS */}
      {/* ================================================================= */}
      <CollapsibleCard title="11. Layout Patterns (レイアウトパターン)">
        <div className="space-y-8">
          {/* Search Form Layout */}
          <div>
            <h4 className="mb-3 font-bold">Search Form Layout (検索フォーム)</h4>
            <div className="p-4 rounded-lg bg-gray-50">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">番号</label>
                  <AppTextField className="w-full" placeholder="番号" />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">名前</label>
                  <AppTextField className="w-full" placeholder="名前" />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">ステータス</label>
                  <AppSelect
                    className="w-full"
                    options={[
                      { value: '', label: 'すべて' },
                      { value: 'active', label: '有効' },
                      { value: 'inactive', label: '無効' },
                    ]}
                  />
                </div>
                <div className="flex items-end">
                  <AppButton
                    type="primary"
                    className="flex items-center justify-center w-full gap-2 px-4 py-2 font-medium text-white rounded"
                    style={{ backgroundColor: colors.secondary }}
                  >
                    🔍 検索
                  </AppButton>
                </div>
              </div>
            </div>
          </div>

          {/* Detail View Layout */}
          <div>
            <h4 className="mb-3 font-bold">Detail View Layout (詳細表示)</h4>
            <div className="overflow-hidden bg-white border rounded-lg">
              <table className="w-full">
                <tbody>
                  <tr className="border-b">
                    <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">ID</th>
                    <td className="px-4 py-3 text-sm">12345</td>
                  </tr>
                  <tr className="border-b">
                    <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">
                      名前
                    </th>
                    <td className="px-4 py-3 text-sm">山田 太郎</td>
                  </tr>
                  <tr className="border-b">
                    <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">
                      メール
                    </th>
                    <td className="px-4 py-3 text-sm">yamada@example.com</td>
                  </tr>
                  <tr>
                    <th className="w-1/4 px-4 py-3 text-sm font-medium text-left bg-gray-50">
                      登録日
                    </th>
                    <td className="px-4 py-3 text-sm">2026年01月15日</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Edit Form Layout */}
          <div>
            <h4 className="mb-3 font-bold">Edit Form Layout (編集フォーム)</h4>
            <div className="p-4 bg-white border rounded-lg">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    名前 <span className="text-red-500">*</span>
                  </label>
                  <AppTextField className="w-full" defaultValue="山田 太郎" />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    メール <span className="text-red-500">*</span>
                  </label>
                  <AppTextField className="w-full" type="email" defaultValue="yamada@example.com" />
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 text-sm font-medium text-gray-700">備考</label>
                  <AppTextArea className="w-full" rows={3} placeholder="備考" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 mt-4 border-t">
                <AppButton
                  type="default"
                  className="px-4 py-2 font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                >
                  キャンセル
                </AppButton>
                <AppButton
                  type="primary"
                  className="px-4 py-2 font-medium text-white rounded"
                  style={{ backgroundColor: colors.secondary }}
                >
                  保存
                </AppButton>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* ================================================================= */}
      {/* SECTION 12: LOADING STATES (THEME-AWARE) */}
      {/* ================================================================= */}
      <CollapsibleCard title="12. Loading States (ローディング状態) - Theme-aware">
        <div className="space-y-6">
          {/* Theme-aware Spinner */}
          <div>
            <p className="mb-3 text-sm font-medium text-gray-700">Theme-aware Spinners</p>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 border-4 rounded-full animate-spin"
                  style={{
                    borderColor: `${colors.secondary}30`,
                    borderTopColor: colors.secondary,
                  }}
                ></div>
                <span className="text-sm text-gray-600">Secondary</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 border-4 rounded-full animate-spin"
                  style={{
                    borderColor: `${colors.primary}30`,
                    borderTopColor: colors.primary,
                  }}
                ></div>
                <span className="text-sm text-gray-600">Primary</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 border-4 rounded-full animate-spin"
                  style={{
                    borderColor: `${colors.sectionHeader}30`,
                    borderTopColor: colors.sectionHeader,
                  }}
                ></div>
                <span className="text-sm text-gray-600">Section</span>
              </div>
            </div>
          </div>

          {/* Skeleton */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Skeleton Loading:</p>
            <div className="space-y-3">
              <AppSkeleton active paragraph={{ rows: 3 }} />
            </div>
          </div>

          {/* Theme-aware Progress Bars */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Theme-aware Progress Bars</p>
            <div className="space-y-4">
              <div>
                <p className="mb-1 text-xs text-gray-500">Secondary Color ({colors.secondary})</p>
                <AppProgress percent={75} strokeColor={colors.secondary} status="active" />
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-500">Primary Color ({colors.primary})</p>
                <AppProgress percent={50} strokeColor={colors.primary} status="active" />
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-500">
                  Section Header Color ({colors.sectionHeader})
                </p>
                <AppProgress percent={90} strokeColor={colors.sectionHeader} status="active" />
              </div>
            </div>
          </div>

          {/* Inline Loading Button */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Loading Button (Theme-aware)</p>
            <AppButton
              loading
              disabled
              style={{ backgroundColor: colors.secondary, color: '#fff' }}
            >
              Loading...
            </AppButton>
          </div>

          {/* AppTooltip */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">AppTooltip</p>
            <p className="mb-3 text-sm text-gray-600">
              Wrapper của Ant Design Tooltip. Dùng để hiển thị gợi ý khi hover/focus. Props: title
              (nội dung), placement (top | bottom | left | right), v.v.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <AppTooltip title="ツールチップのテキスト">
                <AppButton type="primary">Hover me</AppButton>
              </AppTooltip>
              <AppTooltip title="処理中です。しばらくお待ちください。" placement="bottom">
                <AppButton style={{ backgroundColor: colors.secondary, color: '#fff' }}>
                  Secondary + Tooltip
                </AppButton>
              </AppTooltip>
              <AppTooltip title="無効な要素は span でラップすると表示されます">
                <span>
                  <AppButton disabled>Disabled + Tooltip</AppButton>
                </span>
              </AppTooltip>
            </div>
          </div>
        </div>
      </CollapsibleCard>
    </div>
  );
};

export default ClinicDesignTemplatePage;
